import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Form, Table, Badge, Alert, Modal, Spinner } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import facilitatorService from '../services/facilitatorService';
import programsService from '../services/programsService';
import attendanceService from '../services/attendanceService';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, query, orderBy, getDocs, limit, where } from 'firebase/firestore';
import { formatDateTime } from '../utils/dateFormatter';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';

const ProgressReportGenerator = ({
  learners: providedLearners = null,
  programs: providedPrograms = null,
  useProvidedData = false,
  reportHistoryUserId = null
}) => {
  const { currentUser } = useAuth();
  const [selectedLearner, setSelectedLearner] = useState('');

  // Add print styles to head when component mounts
  useEffect(() => {
    const styleId = 'progress-report-print-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @media print {
          body * {
            visibility: hidden;
          }
          .print-content, .print-content * {
            visibility: visible;
          }
          .print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .modal-header, .modal-footer, .btn, button {
            display: none !important;
          }
          .modal-body {
            max-height: none !important;
            overflow: visible !important;
          }
          @page {
            margin: 2cm;
          }
        }
      `;
      document.head.appendChild(style);
    }
    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);
  const [reportType, setReportType] = useState('individual');
  const [showModal, setShowModal] = useState(false);
  const [generatedReport, setGeneratedReport] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportHistory, setReportHistory] = useState([]);
  const [learners, setLearners] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  const useMockData = process.env.REACT_APP_USE_MOCK_DATA === 'true';

  // Load data on component mount
  useEffect(() => {
    if (currentUser) {
      if (useProvidedData) {
        const safeLearners = Array.isArray(providedLearners) ? providedLearners : [];
        const safePrograms = Array.isArray(providedPrograms) ? providedPrograms : [];
        setLearners(safeLearners);
        setPrograms(safePrograms);
        setLoading(false);
        loadReportHistory(safeLearners, reportHistoryUserId);
      } else {
        loadData();
      }
    }
  }, [currentUser, useProvidedData, providedLearners, providedPrograms, reportHistoryUserId]);

  // Load all required data
  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadLearners(),
        loadPrograms(),
        loadReportHistory()
      ]);
    } catch (error) {
      devError('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load learners from facilitator's assigned programs
  const loadLearners = async () => {
    try {
      const learnersData = await facilitatorService.getLearnersByAssignedPrograms(currentUser.uid);
      setLearners(learnersData);
    } catch (error) {
      devError('Error loading learners:', error);
    }
  };

  // Load programs
  const loadPrograms = async () => {
    try {
      const programsData = await programsService.getPrograms(true);
      setPrograms(programsData);
    } catch (error) {
      devError('Error loading programs:', error);
    }
  };

  // Load report history from Firebase progressReports collection
  const loadReportHistory = async (allowedLearners = null, filterUserId = null) => {
    if (useMockData) {
      setReportHistory([
        {
          id: 'report_1',
          type: 'individual',
          generatedAt: new Date().toISOString(),
          generatedBy: 'Mock User'
        }
      ]);
      return;
    }

    try {
      const allowedIds = new Set(
        Array.isArray(allowedLearners)
          ? allowedLearners.map(learner => learner?.id || learner?.uid).filter(Boolean)
          : []
      );
      const collectionRef = collection(db, 'progressReports');
      const q = filterUserId
        ? query(collectionRef, where('generatedById', '==', filterUserId), limit(10))
        : query(collectionRef, orderBy('generatedAt', 'desc'), limit(10));
      
      const querySnapshot = await getDocs(q);
      const reports = [];
      
      querySnapshot.forEach((doc) => {
        reports.push({
          id: doc.id,
          ...doc.data(),
          generatedAt: doc.data().generatedAt?.toDate()?.toISOString() || null
        });
      });
      
      if (allowedIds.size > 0) {
        const filtered = reports.filter((report) => {
          const learnerIds = report.learnerIds || [];
          if (learnerIds.some(id => allowedIds.has(id))) return true;

          const reportLearner = report.reportData?.learner;
          if (reportLearner) {
            const reportLearnerId = reportLearner.id || reportLearner.uid;
            if (allowedIds.has(reportLearnerId)) return true;
          }

          const cohortData = report.reportData?.cohortData;
          if (Array.isArray(cohortData)) {
            return cohortData.some((entry) => {
              const learnerId = entry?.learner?.id || entry?.learner?.uid;
              return allowedIds.has(learnerId);
            });
          }

          return false;
        });
        setReportHistory(filtered);
      } else if (allowedLearners) {
        setReportHistory([]);
      } else {
        setReportHistory(reports);
      }
    } catch (error) {
      devError('Error loading report history:', error);
    }
  };

  // Save report to Firebase progressReports collection
  const saveReportToFirebase = async (reportData) => {
    if (useMockData) {
      devLog('🔧 Mock: Would save report to Firebase progressReports collection');
      return;
    }

    try {
      const reportDoc = {
        type: reportType,
        reportData,
        generatedAt: serverTimestamp(),
        generatedBy: currentUser?.email || 'Current User',
        generatedById: currentUser?.uid || null,
        learnerIds: reportType === 'individual'
          ? [selectedLearner]
          : learners.map(l => l.id || l.uid).filter(Boolean),
        metadata: {
          version: '1.0',
          format: 'detailed',
          includesRealTimeData: true
        }
      };

      const docRef = await addDoc(collection(db, 'progressReports'), reportDoc);
      devLog('Progress report saved to Firebase:', docRef.id);
      
      // Refresh report history
      await loadReportHistory();
      
      return docRef.id;
    } catch (error) {
      devError('Error saving report to Firebase:', error);
    }
  };

  const loadLearnerAttendance = async (learnerId) => {
    try {
      const attendanceRef = collection(db, 'users', learnerId, 'attendance');
      const snapshot = await getDocs(attendanceRef);
      const records = snapshot.docs.map((docSnap) => docSnap.data());
      return records.map((record) => ({
        ...record,
        date: record.date?.toDate ? record.date.toDate() : record.date
      }));
    } catch (error) {
      devError('Error loading learner attendance:', error);
      return [];
    }
  };

  const generateIndividualReport = async (learnerId) => {
    const learner = learners.find(l => l.id === learnerId || l.uid === learnerId);
    if (!learner) {
      throw new Error('Learner not found');
    }

    const progress = learner.moduleProgress || {};
    const userAssignments = learner.assignments || {};
    const userDocuments = learner.documents || {};
    const userAttendance = learner.attendance && learner.attendance.length
      ? learner.attendance
      : await loadLearnerAttendance(learnerId);

    // Module Progress - Get modules from learner's program
    const learnerProgram = programs.find(p => p.id === learner.programId);
    const programModules = learnerProgram?.modules || [];

    const moduleDetails = programModules.map(moduleId => {
      const moduleProgress = progress[moduleId] || {};
      const isCompleted = moduleProgress.completed === true;
      return {
        id: moduleId,
        name: moduleId, // We'll display module ID for now, could be enhanced later
        progress: moduleProgress.progress || 0,
        timeSpent: moduleProgress.timeSpent || 0,
        status: isCompleted ? 'Completed' : 'In Progress',
        completedAt: moduleProgress.completedAt
      };
    });

    // Assignment Progress
    const assignmentDetails = Object.keys(userAssignments).map(assignmentId => {
      const assignment = userAssignments[assignmentId];
      return {
        id: assignmentId,
        name: assignmentId, // We'll display assignment ID for now
        studentProgress: assignment.progress || 0,
        studentStatus: assignment.status || 'not_started',
        score: assignment.score,
        submittedAt: assignment.submittedAt
      };
    });

    // Document Status - Use all document categories from the system
    // This ensures we pick up all documents including conditional ones like medical certificates
    const allDocumentCategories = [
      'Certified ID Copy - not older than 3 months',
      'Certified Affidavit confirming Unemployment – not Older than 3 months',
      'Certified Matric Certificate - not older than 3 months',
      'Certified Highest Qualification - not older than 3 months',
      'Media Release Form - available for online submission or hard copy',
      'Proof of Criminal Checks - we will reimburse with proof of payment and application',
      'Completed EEA1 Form - available for online submission or hard copy',
      'Official Disability Medical Certificate - If Applicable',
      'Training Agreement - online or hard copy available',
      'Internship/Learnership Agreement',
      'Workplace Agreement',
      'Proof of Stipend payment',
      'Other Documents'
    ];

    const documentStatus = allDocumentCategories.map(category => {
      // Try to find document by category name, categoryKey, or categoryId
      let doc = userDocuments[category];
      if (!doc) {
        // Try with different key variations
        const categoryKey = Object.keys(userDocuments).find(key =>
          userDocuments[key]?.category === category ||
          userDocuments[key]?.categoryId?.toLowerCase().replace(/-/g, '') === category.toLowerCase().replace(/[^a-z0-9]/g, '') ||
          key.toLowerCase().replace(/[^a-z0-9]/g, '').includes(category.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 15))
        );
        doc = categoryKey ? userDocuments[categoryKey] : null;
      }

      return {
        category,
        status: doc ? doc.status : 'missing',
        uploadedAt: doc?.uploadedAt || doc?.uploadDate,
        rejectionComment: doc?.rejectionComment,
        documentURL: doc?.downloadURL || doc?.fileURL
      };
    });

    // Attendance Summary
    const totalSessions = userAttendance.length;
    const attendedSessions = userAttendance.filter(a => a.status === 'present').length;
    const attendanceRate = totalSessions > 0 ? Math.round((attendedSessions / totalSessions) * 100) : 0;

    const report = {
      learner,
      moduleDetails,
      assignmentDetails,
      documentStatus,
      attendanceSummary: {
        totalSessions,
        attendedSessions,
        attendanceRate,
        recentAttendance: userAttendance.slice(-5)
      },
      overallMetrics: {
        moduleCompletionRate: programModules.length > 0 ? Math.round((moduleDetails.filter(m => m.status === 'Completed').length / programModules.length) * 100) : 0,
        assignmentCompletionRate: assignmentDetails.length > 0 ? Math.round((assignmentDetails.filter(a => a.studentStatus === 'completed' || a.studentStatus === 'graded').length / assignmentDetails.length) * 100) : 0,
        documentCompletionRate: Math.round((documentStatus.filter(d => d.status === 'verified').length / allDocumentCategories.length) * 100),
        totalStudyTime: Object.values(progress).reduce((sum, mp) => sum + (mp.timeSpent || 0), 0)
      },
      generatedAt: new Date().toISOString(),
      dataSource: useMockData ? 'mock' : 'firebase'
    };

    // Save to Firebase progressReports collection
    await saveReportToFirebase(report);

    return report;
  };

  const generateCohortReport = async () => {
    const attendanceMap = new Map(
      await Promise.all(
        learners.map(async (learner) => {
          const learnerId = learner.id || learner.uid;
          const attendance = learner.attendance && learner.attendance.length
            ? learner.attendance
            : await loadLearnerAttendance(learnerId);
          return [learnerId, attendance];
        })
      )
    );
    const pendingSessionsMap = new Map(
      await Promise.all(
        learners.map(async (learner) => {
          const learnerId = learner.id || learner.uid;
          const pendingSessions = await attendanceService.getPendingSessionsForLearner(
            learnerId,
            learner?.cohortId || null
          ).catch(() => ({ sessions: [] }));
          return [learnerId, pendingSessions?.sessions || []];
        })
      )
    );

    const cohortData = learners.map(learner => {
      const progress = learner.moduleProgress || {};
      const userAssignments = learner.assignments || {};
      const userDocuments = learner.documents || {};
      const userAttendance = attendanceMap.get(learner.id || learner.uid) || [];
      const pendingSessions = pendingSessionsMap.get(learner.id || learner.uid) || [];

      // Get modules from learner's program
      const learnerProgram = programs.find(p => p.id === learner.programId);
      const programModules = learnerProgram?.modules || [];

      let completedModules = 0;
      if (programModules.length > 0) {
        completedModules = programModules.filter(moduleId => {
          const moduleProgress = progress[moduleId];
          return moduleProgress && moduleProgress.completed === true;
        }).length;
      }

      const totalSessions = userAttendance.length;
      const attendedSessions = userAttendance.filter(a => a.status === 'present').length;

      const assignmentDetails = Object.keys(userAssignments);
      const completedAssignments = assignmentDetails.filter(assignmentId => {
        const assignment = userAssignments[assignmentId];
        return assignment.status === 'completed' || assignment.status === 'graded';
      }).length;
      const outstandingAssignments = assignmentDetails.filter((assignmentId) => {
        const assignment = userAssignments[assignmentId];
        return !['completed', 'graded'].includes(assignment?.status);
      });
      const submittedDocuments = Object.values(userDocuments).filter((doc) => doc?.status && doc.status !== 'missing');
      const outstandingDocuments = Object.values(userDocuments).filter((doc) => !doc?.status || doc.status === 'missing');

      return {
        learner,
        moduleCompletionRate: programModules.length > 0 ? Math.round((completedModules / programModules.length) * 100) : 0,
        assignmentCompletionRate: assignmentDetails.length > 0 ? Math.round((completedAssignments / assignmentDetails.length) * 100) : 0,
        documentCompletionRate: Math.round((Object.values(userDocuments).filter(d => d.status === 'verified').length / 11) * 100),
        attendanceRate: totalSessions > 0 ? Math.round((attendedSessions / totalSessions) * 100) : 0,
        totalStudyTime: Object.values(progress).reduce((sum, mp) => sum + (mp.timeSpent || 0), 0),
        submittedRegisters: attendedSessions,
        missingRegisters: pendingSessions.length,
        missingRegisterDates: pendingSessions
          .map((session) => {
            const date = session?.scheduledDate?.toDate ? session.scheduledDate.toDate() : session?.scheduledDate;
            return date ? new Date(date).toLocaleDateString('en-ZA') : '';
          })
          .filter(Boolean),
        submittedDocuments: submittedDocuments.map((doc) => doc.category || doc.categoryId || 'Document'),
        outstandingDocuments: outstandingDocuments.map((doc) => doc.category || doc.categoryId || 'Document'),
        assignmentStatusSummary: Object.entries(userAssignments)
          .map(([assignmentId, assignment]) => `${assignment.title || assignment.name || assignmentId}: ${assignment.status || 'unknown'}`)
          .join(' | '),
        outstandingAssignments: outstandingAssignments.map((assignmentId) => {
          const assignment = userAssignments[assignmentId];
          return assignment?.title || assignment?.name || assignmentId;
        })
      };
    });

    const cohortAverages = {
      moduleCompletion: Math.round(cohortData.reduce((sum, l) => sum + l.moduleCompletionRate, 0) / cohortData.length),
      assignmentCompletion: Math.round(cohortData.reduce((sum, l) => sum + l.assignmentCompletionRate, 0) / cohortData.length),
      documentCompletion: Math.round(cohortData.reduce((sum, l) => sum + l.documentCompletionRate, 0) / cohortData.length),
      attendance: Math.round(cohortData.reduce((sum, l) => sum + l.attendanceRate, 0) / cohortData.length),
      studyTime: Math.round(cohortData.reduce((sum, l) => sum + l.totalStudyTime, 0) / cohortData.length)
    };

    const cohortReport = { 
      cohortData, 
      cohortAverages,
      generatedAt: new Date().toISOString(),
      dataSource: useMockData ? 'mock' : 'firebase'
    };

    // Save to Firebase progressReports collection
    await saveReportToFirebase(cohortReport);

    return cohortReport;
  };

  const handleGenerateReport = async () => {
    setIsGenerating(true);

    try {
      if (reportType === 'individual' && selectedLearner) {
        const report = await generateIndividualReport(selectedLearner);
        setGeneratedReport({ type: 'individual', data: report });
      } else if (reportType === 'cohort' || reportType === 'detailed-cohort') {
        const report = await generateCohortReport();
        setGeneratedReport({ type: reportType, data: report });
      }
      setShowModal(true);
    } catch (error) {
      devError('Error generating report:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const csvEscape = (value) => {
    const text = String(value ?? '');
    if (/[",\n]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  };

  const downloadCsv = (rows, filename) => {
    const csvContent = rows.map((row) => row.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const buildReportCsvRows = () => {
    if (!generatedReport?.data) return [];

    if (generatedReport.type === 'individual') {
      const learner = generatedReport.data.learner || {};
      const submittedDocs = (generatedReport.data.documentStatus || []).filter((item) => item.status && item.status !== 'missing');
      const missingDocs = (generatedReport.data.documentStatus || []).filter((item) => item.status === 'missing');
      const assignmentSummary = (generatedReport.data.assignmentDetails || [])
        .map((assignment) => `${assignment.name || assignment.title || assignment.id}: ${assignment.studentStatus || 'unknown'}`)
        .join(' | ');

      return [
        ['Full Name', 'ID Number', 'Email', 'Phone', 'Programme', 'Submitted Documents', 'Outstanding Documents', 'Submitted Registers', 'Missing Registers', 'Missing Register Dates', 'Assignment Status Summary', 'Attendance Rate'],
        [
          learner.displayName || learner.name || '',
          learner.idNumber || '',
          learner.email || '',
          learner.phone || learner.phoneNumber || '',
          learner.programName || learner.program || '',
          submittedDocs.map((item) => item.category).join(' | '),
          missingDocs.map((item) => item.category).join(' | '),
          generatedReport.data.attendanceSummary?.attendedSessions || 0,
          Math.max((generatedReport.data.attendanceSummary?.totalSessions || 0) - (generatedReport.data.attendanceSummary?.attendedSessions || 0), 0),
          ((generatedReport.data.attendanceSummary?.recentAttendance || []).filter((entry) => entry.status !== 'present').map((entry) => formatDateTime(entry.date)).join(' | ')),
          assignmentSummary,
          `${generatedReport.data.attendanceSummary?.attendanceRate || 0}%`
        ]
      ];
    }

    if (generatedReport.type === 'cohort' || generatedReport.type === 'detailed-cohort') {
      const rows = [
        ['Full Name', 'ID Number', 'Email', 'Phone', 'Programme', 'Module Completion %', 'Assignment Completion %', 'Document Completion %', 'Attendance %', 'Study Time', 'Submitted Documents', 'Outstanding Documents', 'Submitted Registers', 'Missing Registers', 'Missing Register Dates', 'Assignment Status Summary', 'Outstanding Assignments']
      ];

      (generatedReport.data.cohortData || []).forEach((item) => {
        const learner = item.learner || {};
        rows.push([
          learner.displayName || learner.name || '',
          learner.idNumber || '',
          learner.email || '',
          learner.phone || learner.phoneNumber || '',
          learner.programName || learner.program || '',
          item.moduleCompletionRate || 0,
          item.assignmentCompletionRate || 0,
          item.documentCompletionRate || 0,
          item.attendanceRate || 0,
          formatTime(item.totalStudyTime || 0),
          (item.submittedDocuments || []).join(' | '),
          (item.outstandingDocuments || []).join(' | '),
          item.submittedRegisters || 0,
          item.missingRegisters || 0,
          (item.missingRegisterDates || []).join(' | '),
          item.assignmentStatusSummary || '',
          (item.outstandingAssignments || []).join(' | ')
        ]);
      });

      return rows;
    }

    return [];
  };

  const downloadCSV = () => {
    const rows = buildReportCsvRows();
    if (!rows.length) {
      alert('No report data available to export.');
      return;
    }
    const suffix = generatedReport?.type || 'report';
    downloadCsv(rows, `progress_report_${suffix}_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const getStatusBadge = (status) => {
    const variants = {
      completed: 'success',
      'in_progress': 'primary',
      'not_started': 'secondary',
      verified: 'success',
      pending: 'warning',
      rejected: 'danger',
      missing: 'secondary',
      present: 'success',
      absent: 'danger',
      late: 'warning'
    };
    return variants[status] || 'secondary';
  };

  const downloadPDF = () => {
    if (!generatedReport || !generatedReport.data) {
      alert('No report data available to download');
      return;
    }

    try {
      const doc = new jsPDF();
      const data = generatedReport.data;
      let yPos = 20;

      if (generatedReport.type === 'individual') {
        // Header
        doc.setFontSize(16);
        doc.text('Individual Progress Report', 105, yPos, { align: 'center' });
        yPos += 10;

        // Learner Info
        doc.setFontSize(12);
        doc.text(`Learner: ${data.learner?.displayName || 'Unknown'}`, 20, yPos);
        yPos += 7;
        doc.setFontSize(10);
        doc.text(`Email: ${data.learner?.email || 'N/A'}`, 20, yPos);
        yPos += 7;

        // Safe date formatting
        const enrolledDate = data.learner?.createdAt || data.learner?.enrolledDate || data.learner?.registrationDate;
        const enrolledDateStr = enrolledDate ? formatDateTime(enrolledDate) : 'N/A';
        doc.text(`Enrolled: ${enrolledDateStr}`, 20, yPos);
        yPos += 10;

        // Overall Metrics
        doc.setFontSize(11);
        doc.text('Overall Progress', 20, yPos);
        yPos += 7;
        doc.setFontSize(10);
        doc.text(`Module Completion: ${data.overallMetrics?.moduleCompletionRate || 0}%`, 20, yPos);
        yPos += 5;
        doc.text(`Assignment Completion: ${data.overallMetrics?.assignmentCompletionRate || 0}%`, 20, yPos);
        yPos += 5;
        doc.text(`Document Completion: ${data.overallMetrics?.documentCompletionRate || 0}%`, 20, yPos);
        yPos += 10;

        // Document Status Table
        if (data.documentStatus && data.documentStatus.length > 0) {
          doc.setFontSize(11);
          doc.text('Document Status', 20, yPos);
          yPos += 5;

          const documentTableData = data.documentStatus.map(docItem => [
            docItem.category || 'Unknown',
            docItem.status || 'unknown',
            docItem.uploadedAt ? formatDateTime(docItem.uploadedAt) : 'N/A',
            docItem.rejectionComment || 'N/A'
          ]);

          autoTable(doc, {
            startY: yPos,
            head: [['Document Type', 'Status', 'Uploaded Date', 'Comments']],
            body: documentTableData,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [66, 139, 202] },
            columnStyles: {
              0: { cellWidth: 70 },
              1: { cellWidth: 25 },
              2: { cellWidth: 45 },
              3: { cellWidth: 45 }
            },
            margin: { left: 20, right: 20 }
          });

          yPos = doc.lastAutoTable.finalY + 10;
        }

        // Attendance Summary (if space available)
        if (yPos < 250 && data.attendanceSummary) {
          doc.setFontSize(11);
          doc.text('Attendance Summary', 20, yPos);
          yPos += 7;
          doc.setFontSize(10);
          const attendanceRate = data.attendanceSummary.attendanceRate || 0;
          const attendedSessions = data.attendanceSummary.attendedSessions || 0;
          const totalSessions = data.attendanceSummary.totalSessions || 0;
          doc.text(`Attendance Rate: ${attendanceRate}% (${attendedSessions}/${totalSessions} sessions)`, 20, yPos);
        }

        // Save the PDF
        const learnerName = data.learner?.displayName?.replace(/\s+/g, '_') || 'Unknown';
        const fileName = `Progress_Report_${learnerName}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);

      } else if (generatedReport.type === 'detailed-cohort') {
        // Detailed Cohort Report - Each learner on separate pages
        doc.setFontSize(16);
        doc.text('Detailed Cohort Report', 105, 15, { align: 'center' });
        doc.setFontSize(10);
        doc.text(`Generated: ${formatDateTime(new Date())}`, 105, 22, { align: 'center' });

        if (data.cohortData && data.cohortData.length > 0) {
          data.cohortData.forEach((item, index) => {
            if (index > 0) {
              doc.addPage(); // New page for each learner
            }

            let yPos = 30;
            const learner = item.learner;

            // Learner Header
            doc.setFontSize(14);
            doc.text(`${learner?.displayName || 'Unknown Learner'}`, 20, yPos);
            yPos += 7;
            doc.setFontSize(10);
            doc.text(`Email: ${learner?.email || 'N/A'}`, 20, yPos);
            yPos += 5;
            const enrolledDate = learner?.createdAt || learner?.enrolledDate;
            doc.text(`Enrolled: ${enrolledDate ? formatDateTime(enrolledDate) : 'N/A'}`, 20, yPos);
            yPos += 10;

            // Progress Metrics
            doc.setFontSize(11);
            doc.text('Overall Progress', 20, yPos);
            yPos += 7;
            doc.setFontSize(10);
            doc.text(`Module Completion: ${item.moduleCompletionRate || 0}%`, 25, yPos);
            yPos += 5;
            doc.text(`Assignment Completion: ${item.assignmentCompletionRate || 0}%`, 25, yPos);
            yPos += 5;
            doc.text(`Document Completion: ${item.documentCompletionRate || 0}%`, 25, yPos);
            yPos += 5;
            doc.text(`Attendance Rate: ${item.attendanceRate || 0}%`, 25, yPos);
            yPos += 10;

            // Document Status (if available in item)
            if (learner?.documents) {
              doc.setFontSize(11);
              doc.text('Document Status', 20, yPos);
              yPos += 5;

              const docs = Object.values(learner.documents);
              if (docs.length > 0) {
                const docTableData = docs.slice(0, 13).map(d => [
                  d.category || d.categoryId || 'Unknown',
                  d.status || 'unknown',
                  d.uploadedAt || d.uploadDate ? formatDateTime(d.uploadedAt || d.uploadDate) : 'N/A'
                ]);

                autoTable(doc, {
                  startY: yPos,
                  head: [['Document Type', 'Status', 'Uploaded Date']],
                  body: docTableData,
                  styles: { fontSize: 8 },
                  headStyles: { fillColor: [66, 139, 202] },
                  columnStyles: {
                    0: { cellWidth: 80 },
                    1: { cellWidth: 30 },
                    2: { cellWidth: 50 }
                  },
                  margin: { left: 20, right: 20 }
                });
              }
            }

            // Page footer
            doc.setFontSize(8);
            doc.text(`Learner ${index + 1} of ${data.cohortData.length}`, 105, 285, { align: 'center' });
          });
        }

        // Save the PDF
        const fileName = `Detailed_Cohort_Report_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);

      } else if (generatedReport.type === 'cohort') {
        // Header
        doc.setFontSize(16);
        doc.text('Cohort Summary Report', 105, yPos, { align: 'center' });
        yPos += 10;

        // Cohort Averages
        doc.setFontSize(12);
        doc.text('Cohort Averages', 20, yPos);
        yPos += 7;
        doc.setFontSize(10);

        if (data.cohortAverages) {
          doc.text(`Average Module Completion: ${data.cohortAverages.moduleCompletion || 0}%`, 20, yPos);
          yPos += 5;
          doc.text(`Average Assignment Completion: ${data.cohortAverages.assignmentCompletion || 0}%`, 20, yPos);
          yPos += 5;
          doc.text(`Average Document Completion: ${data.cohortAverages.documentCompletion || 0}%`, 20, yPos);
          yPos += 5;
          doc.text(`Average Attendance Rate: ${data.cohortAverages.attendance || 0}%`, 20, yPos);
          yPos += 10;
        }

        // Learner Summary Table
        if (data.cohortData && data.cohortData.length > 0) {
          doc.setFontSize(11);
          doc.text('Learner Summary', 20, yPos);
          yPos += 5;

          const cohortTableData = data.cohortData.map(item => [
            item.learner?.displayName || 'Unknown',
            `${item.moduleCompletionRate || 0}%`,
            `${item.assignmentCompletionRate || 0}%`,
            `${item.documentCompletionRate || 0}%`,
            `${item.attendanceRate || 0}%`
          ]);

          autoTable(doc, {
            startY: yPos,
            head: [['Learner', 'Modules', 'Assignments', 'Documents', 'Attendance']],
            body: cohortTableData,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [66, 139, 202] },
            columnStyles: {
              0: { cellWidth: 60 },
              1: { cellWidth: 30 },
              2: { cellWidth: 30 },
              3: { cellWidth: 30 },
              4: { cellWidth: 30 }
            },
            margin: { left: 20, right: 20 }
          });
        }

        // Save the PDF
        const fileName = `Cohort_Report_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
      }

    } catch (error) {
      devError('Error generating PDF:', error);
      devError('Report data:', generatedReport);
      alert(`Error generating PDF: ${error.message}. Please try again or use the Print option.`);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="progress-report-generator">
      <Card>
        <Card.Header>
          <h5 className="mb-0">Progress Report Generator</h5>
        </Card.Header>
        <Card.Body>
          <Row className="mb-4">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Report Type</Form.Label>
                <Form.Select value={reportType} onChange={(e) => setReportType(e.target.value)}>
                  <option value="individual">Individual Learner Report</option>
                  <option value="cohort">Cohort Summary Report</option>
                  <option value="detailed-cohort">Detailed Cohort Report (All Learners)</option>
                </Form.Select>
              </Form.Group>
            </Col>
            {reportType === 'individual' && (
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Select Learner</Form.Label>
                  <Form.Select value={selectedLearner} onChange={(e) => setSelectedLearner(e.target.value)}>
                    <option value="">Choose a learner...</option>
                    {learners.map(learner => (
                      <option key={learner.id || learner.uid} value={learner.id || learner.uid}>
                        {learner.displayName} ({learner.email})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            )}
            <Col md={4} className="d-flex align-items-end">
              <Button 
                variant="primary" 
                onClick={handleGenerateReport}
                disabled={(reportType === 'individual' && !selectedLearner) || isGenerating}
              >
                {isGenerating && <Spinner as="span" animation="border" size="sm" className="me-2" />}
                {isGenerating ? 'Generating...' : 'Generate Report'}
              </Button>
            </Col>
          </Row>

          <Alert variant="info">
            <h6>Report Features:</h6>
            <ul className="mb-0">
              <li><strong>Individual Reports:</strong> Detailed progress for a specific learner including modules, assignments, documents, and attendance</li>
              <li><strong>Cohort Summary:</strong> Summary statistics and comparison across all learners in a single page</li>
              <li><strong>Detailed Cohort:</strong> Multi-page PDF with each learner's detailed report on separate pages</li>
              <li><strong>Export Options:</strong> Download as PDF or print for offline use</li>
              <li><strong>Real-time Data:</strong> Reports include live Firebase data and are saved for future reference</li>
            </ul>
          </Alert>

          {/* Report History */}
          {reportHistory.length > 0 && (
            <Card className="mb-3">
              <Card.Header>
                <h6 className="mb-0">Recent Reports</h6>
              </Card.Header>
              <Card.Body>
                <Table size="sm" responsive>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Generated</th>
                      <th>Generated By</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportHistory.slice(0, 5).map((report) => (
                      <tr key={report.id}>
                        <td>
                          <Badge bg={report.type === 'individual' ? 'primary' : 'success'}>
                            {report.type}
                          </Badge>
                        </td>
                        <td>{formatDateTime(report.generatedAt)}</td>
                        <td>{report.generatedBy}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => {
                              setGeneratedReport({ type: report.type, data: report.reportData });
                              setShowModal(true);
                            }}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}
        </Card.Body>
      </Card>

      {/* Report Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            {generatedReport?.type === 'individual' ? 'Individual Progress Report' :
             generatedReport?.type === 'detailed-cohort' ? 'Detailed Cohort Report' :
             'Cohort Summary Report'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {generatedReport?.type === 'individual' && generatedReport.data && (
            <div className="print-content">
              <div className="mb-4">
                <h5>{generatedReport.data.learner.displayName}</h5>
                <p className="text-muted">{generatedReport.data.learner.email}</p>
                <p>Enrolled: {formatDateTime(generatedReport.data.learner.createdAt || generatedReport.data.learner.enrolledDate || generatedReport.data.learner.registrationDate)}</p>
              </div>

              <Row className="mb-4">
                <Col md={3} className="text-center">
                  <h4 className="text-info">{generatedReport.data.overallMetrics.moduleCompletionRate}%</h4>
                  <small>Module Completion</small>
                </Col>
                <Col md={3} className="text-center">
                  <h4 className="text-success">{generatedReport.data.overallMetrics.assignmentCompletionRate}%</h4>
                  <small>Assignment Completion</small>
                </Col>
                <Col md={3} className="text-center">
                  <h4 className="text-warning">{generatedReport.data.overallMetrics.documentCompletionRate}%</h4>
                  <small>Document Completion</small>
                </Col>
                <Col md={3} className="text-center">
                  <h4 className="text-primary">{formatTime(generatedReport.data.overallMetrics.totalStudyTime)}</h4>
                  <small>Total Study Time</small>
                </Col>
              </Row>

              <h6>Module Progress</h6>
              <Table striped bordered hover size="sm" className="mb-4">
                <thead>
                  <tr>
                    <th>Module</th>
                    <th>Progress</th>
                    <th>Status</th>
                    <th>Time Spent</th>
                  </tr>
                </thead>
                <tbody>
                  {generatedReport.data.moduleDetails.map(module => (
                    <tr key={module.id}>
                      <td>{module.moduleName}</td>
                      <td>{module.progress}%</td>
                      <td><Badge bg={getStatusBadge(module.status.toLowerCase().replace(' ', '_'))}>{module.status}</Badge></td>
                      <td>{formatTime(module.timeSpent)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <h6>Assignment Progress</h6>
              <Table striped bordered hover size="sm" className="mb-4">
                <thead>
                  <tr>
                    <th>Assignment</th>
                    <th>Status</th>
                    <th>Progress</th>
                    <th>Score</th>
                    <th>Due Date</th>
                  </tr>
                </thead>
                <tbody>
                  {generatedReport.data.assignmentDetails.map(assignment => (
                    <tr key={assignment.id}>
                      <td>{assignment.title}</td>
                      <td><Badge bg={getStatusBadge(assignment.studentStatus)}>{assignment.studentStatus.replace('_', ' ')}</Badge></td>
                      <td>{assignment.studentProgress}%</td>
                      <td>{assignment.score || 'N/A'}</td>
                      <td>{new Date(assignment.dueDate).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <h6>Document Status</h6>
              <Table striped bordered hover size="sm" className="mb-4">
                <thead>
                  <tr>
                    <th>Document Type</th>
                    <th>Status</th>
                    <th>Uploaded Date</th>
                    <th>Comments</th>
                  </tr>
                </thead>
                <tbody>
                  {generatedReport.data.documentStatus.map(doc => (
                    <tr key={doc.category}>
                      <td>{doc.category}</td>
                      <td><Badge bg={getStatusBadge(doc.status)}>{doc.status}</Badge></td>
                      <td>{formatDateTime(doc.uploadedAt)}</td>
                      <td>{doc.rejectionComment || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <h6>Attendance Summary</h6>
              <p>Attendance Rate: <strong>{generatedReport.data.attendanceSummary.attendanceRate}%</strong> ({generatedReport.data.attendanceSummary.attendedSessions}/{generatedReport.data.attendanceSummary.totalSessions} sessions)</p>
            </div>
          )}

          {generatedReport?.type === 'cohort' && generatedReport.data && (
            <div className="print-content">
              <h5>Cohort Averages</h5>
              <Row className="mb-4">
                <Col md={2} className="text-center">
                  <h4 className="text-info">{generatedReport.data.cohortAverages.moduleCompletion}%</h4>
                  <small>Module Completion</small>
                </Col>
                <Col md={2} className="text-center">
                  <h4 className="text-success">{generatedReport.data.cohortAverages.assignmentCompletion}%</h4>
                  <small>Assignment Completion</small>
                </Col>
                <Col md={2} className="text-center">
                  <h4 className="text-warning">{generatedReport.data.cohortAverages.documentCompletion}%</h4>
                  <small>Document Completion</small>
                </Col>
                <Col md={2} className="text-center">
                  <h4 className="text-danger">{generatedReport.data.cohortAverages.attendance}%</h4>
                  <small>Attendance Rate</small>
                </Col>
                <Col md={4} className="text-center">
                  <h4 className="text-primary">{formatTime(generatedReport.data.cohortAverages.studyTime)}</h4>
                  <small>Avg Study Time</small>
                </Col>
              </Row>

              <h6>Individual Learner Performance</h6>
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Learner</th>
                    <th>Module %</th>
                    <th>Assignment %</th>
                    <th>Document %</th>
                    <th>Attendance %</th>
                    <th>Study Time</th>
                  </tr>
                </thead>
                <tbody>
                  {generatedReport.data.cohortData.map(learner => (
                    <tr key={learner.learner.uid}>
                      <td>{learner.learner.displayName}</td>
                      <td>{learner.moduleCompletionRate}%</td>
                      <td>{learner.assignmentCompletionRate}%</td>
                      <td>{learner.documentCompletionRate}%</td>
                      <td>{learner.attendanceRate}%</td>
                      <td>{formatTime(learner.totalStudyTime)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-success" onClick={downloadCSV}>
            Download CSV
          </Button>
          <Button variant="outline-primary" onClick={() => window.print()}>
            Print Report
          </Button>
          <Button variant="primary" onClick={downloadPDF}>
            Download PDF
          </Button>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProgressReportGenerator;
